import { useCallback, useEffect, useState } from 'react';
import api from '../api/client';
import SimpleMarkdown from '../components/SimpleMarkdown';
import {
  BillingFacts,
  OzonBankMark,
  TransactionsTable,
  formatDateTime,
  formatMoney,
} from './billingShared';

export default function BillingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmingId, setConfirmingId] = useState(null);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupBusy, setTopupBusy] = useState(false);
  const [topupError, setTopupError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/payments/me/');
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось загрузить баланс.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function confirmReport(id) {
    setConfirmingId(id);
    setError('');
    try {
      await api.post(`/payments/lesson-reports/${id}/confirm/`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось подтвердить занятие.');
    } finally {
      setConfirmingId(null);
    }
  }

  async function startTopup(e) {
    e.preventDefault();
    setTopupError('');
    setTopupBusy(true);
    try {
      const res = await api.post('/payments/topup/', { amount: topupAmount });
      if (res.data?.pay_link) {
        window.location.href = res.data.pay_link;
        return;
      }
      setTopupError('Озон не вернул ссылку на оплату.');
    } catch (err) {
      setTopupError(err.response?.data?.detail || 'Не удалось создать платёж.');
    } finally {
      setTopupBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Загрузка баланса…</p>
      </div>
    );
  }

  return (
    <div className="billing-page">
      <div className="page-header">
        <h1>Баланс и оплата</h1>
        <p className="page-subtitle">
          Семейный счёт: предоплата и долг считаются из журнала операций.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {data?.test_mode && (
        <div className="alert">
          Оплата в тестовом режиме: настоящие деньги не списываются, пока не включён боевой режим.
        </div>
      )}

      <BillingFacts data={data} />

      {data?.members?.length > 0 && (
        <section className="billing-section">
          <h2>Кто в этом счёте</h2>
          <ul className="list-simple">
            {data.members.map((m) => (
              <li key={m.id}>
                {m.display_name || m.username}
                {m.is_owner ? ' (владелец)' : ''}
                {m.is_student ? ' — ученик' : ''}
                {m.is_parent ? ' — родитель' : ''}
              </li>
            ))}
          </ul>
        </section>
      )}

      {data?.pending_reports?.length > 0 && (
        <section className="billing-section">
          <h2>Подтвердите занятия</h2>
          <p className="text-secondary">После подтверждения сумма спишется с семейного счёта.</p>
          <ul className="list-simple">
            {data.pending_reports.map((r) => (
              <li key={r.id} className="billing-report-card">
                <div>
                  <strong>{r.topic || 'Занятие'}</strong>
                  {' · '}
                  {r.duration_minutes} мин · {formatMoney(r.amount)}
                  <div className="text-secondary">{r.description}</div>
                  {r.homework ? (
                    <div className="billing-homework">
                      <span>ДЗ:</span>
                      <SimpleMarkdown text={r.homework} />
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={confirmingId === r.id}
                  onClick={() => confirmReport(r.id)}
                >
                  {confirmingId === r.id ? 'Сохранение…' : 'Подтвердить'}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(() => {
        const recent = (data?.recent_lesson_reports || []).filter(
          (r) => r.status !== 'awaiting_student',
        );
        if (!recent.length) return null;
        return (
          <section className="billing-section">
            <h2>Прошедшие занятия</h2>
            <p className="text-secondary">
              Тема, что делали и текстовое ДЗ из журнала оплаты — остаются здесь после подтверждения.
            </p>
            <ul className="billing-lesson-history-list">
              {recent.map((r) => (
                <li key={r.id} className="billing-lesson-history-card">
                  <div className="billing-lesson-history-meta">
                    <strong>{r.topic || 'Занятие'}</strong>
                    {' · '}
                    {formatDateTime(r.created_at)}
                    {' · '}
                    {r.duration_minutes} мин · {formatMoney(r.amount)}
                    {' · '}
                    <span className="text-secondary">{r.status_label}</span>
                  </div>
                  {r.description ? (
                    <div className="billing-report-details">
                      <strong>Что делали:</strong> {r.description}
                    </div>
                  ) : null}
                  {r.homework ? (
                    <div className="billing-report-details billing-homework">
                      <strong>ДЗ:</strong>
                      <SimpleMarkdown text={r.homework} />
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        );
      })()}

      {data?.topup_available && (
        <section className="billing-section">
          <h2 className="billing-topup-title">
            <OzonBankMark />
            Пополнить
          </h2>
          {data.test_mode && (
            <p className="text-secondary">
              Тестовая оплата: деньги не списываются. После проверки в кабинете Озона можно включить боевой режим.
            </p>
          )}
          <form className="billing-topup-form" onSubmit={startTopup}>
            <label>
              Сумма, ₽
              <input
                className="form-input"
                type="number"
                min="1"
                step="0.01"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                required
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={topupBusy}>
              {topupBusy ? 'Создание платежа…' : 'Оплатить'}
            </button>
          </form>
          {topupError && <div className="alert alert-error">{topupError}</div>}
        </section>
      )}

      <section className="billing-section">
        <h2>История операций</h2>
        <TransactionsTable items={data?.transactions} />
      </section>
    </div>
  );
}
